import { SX_API_KEY } from "@/config/env"
import constants from "@/helpers/constants"
import { type Address } from "viem"
import { SX_CONFIG } from "../constants"
import axios from "axios"

// Constants
const IS_DEV = SX_API_KEY !== ""

// Types
interface ApiResponse<T = any> {
  status: string
  data: T
  message?: string
  error?: string
}

interface PrefillOrderPayload {
  // Add specific properties based on your API requirements
  [key: string]: any
}

// Helper functions
const createHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {}

  if (IS_DEV) {
    headers["x-api-key"] = SX_API_KEY || ""
  }

  return headers
}

const createJsonHeaders = (): Record<string, string> => {
  const headers = createHeaders()
  headers["Content-Type"] = "application/json"
  return headers
}

const handleApiResponse = async (response: Response): Promise<any> => {
  if (!response.ok) {
    throw new Error(
      `HTTP error! status: ${response.status} ${response.statusText}`
    )
  }

  return response.json()
}

export const buildUrl = (
  endpoint: string,
  params?: Record<string, string>
): string => {
  // Use proxy URL to avoid CORS errors
  const baseUrl = constants.getSxBetApiUrl()
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint
  let url = `${baseUrl}/${cleanEndpoint}`

  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      searchParams.append(key, value)
    })
    url += `?${searchParams.toString()}`
  }

  return url
}

// API functions
export const fetchMarketTypes = async (
  eventId: string,
  chainId: number
): Promise<ApiResponse> => {
  const url = buildUrl(`/markets/eventId/${eventId}`, {
    chainId: chainId.toString(),
  })
  const response = await fetch(url, {
    headers: createHeaders(),
  })

  return handleApiResponse(response)
}

export const fetchActiveMarkets = async (
  eventId: string,
  chainId: number
): Promise<ApiResponse> => {
  const url = buildUrl("/markets/active", {
    onlyMainLine: "false",
    eventId,
    chainId: chainId.toString(),
  })

  const response = await fetch(url, {
    headers: createHeaders(),
  })

  return handleApiResponse(response)
}

export const fetchPopularMarkets = async (
  chainId: number
): Promise<ApiResponse> => {
  const url = buildUrl("/markets/popular", {
    onlyMainLine: "true",
    chainId: chainId.toString(),
  })

  const response = await fetch(url, {
    headers: createHeaders(),
  })

  return handleApiResponse(response)
}

export const fetchBestMarkets = async (
  marketHashes: string[],
  chainId: number
): Promise<ApiResponse> => {
  if (marketHashes.length === 0) {
    return {
      status: "success",
      data: { bestOdds: [] },
    }
  }

  const TOKEN = SX_CONFIG[chainId].CONTRACTS.TOKEN
  const url = buildUrl("/orders/odds/best", {
    marketHashes: marketHashes.join(","),
    baseToken: TOKEN as Address,
    chainId: chainId.toString(),
  })
  const response = await fetch(url, {
    headers: createHeaders(),
  })

  return handleApiResponse(response)
}

export const fetchOrders = async (
  marketHashes: string[],
  chainId: number
): Promise<ApiResponse> => {
  if (marketHashes.length === 0) {
    return {
      status: "success",
      data: [],
    }
  }

  const TOKEN = SX_CONFIG[chainId].CONTRACTS.TOKEN
  const url = buildUrl("/orders", {
    marketHashes: marketHashes.join(","),
    orderStatus: "ACTIVE",
    baseToken: TOKEN as Address,
    chainId: chainId.toString(),
  })
  const response = await fetch(url, {
    headers: createHeaders(),
  })

  return handleApiResponse(response)
}

export const fetchFixtureStatus = async (
  sportXEventIds: string | string[],
  chainId: number
): Promise<ApiResponse> => {
  const eventIds = Array.isArray(sportXEventIds)
    ? sportXEventIds.join(",")
    : sportXEventIds

  const url = buildUrl("/fixture/status", {
    sportXEventIds: eventIds,
    chainId: chainId.toString(),
  })

  const response = await fetch(url, {
    headers: createHeaders(),
  })

  return handleApiResponse(response)
}

export const prefillOrder = async (
  apiPayload: PrefillOrderPayload,
  chainId: number
): Promise<ApiResponse> => {
  const url = buildUrl("/orders/crosschain/pre-fill", {
    chainId: chainId.toString(),
  })

  const response = await fetch(url, {
    method: "POST",
    body: JSON.stringify(apiPayload),
    headers: createJsonHeaders(),
  })

  const result = await handleApiResponse(response)

  if (result.status !== "success") {
    const errorMessage =
      result?.message || result?.error || JSON.stringify(result)

    if (result?.message === "ORDERS_DONT_EXIST") {
      throw new Error(
        "Order not found: orderHash or token mismatch/or invalid case"
      )
    }

    throw new Error(`Prefill failed: ${errorMessage}`)
  }

  return result
}

export const fetchSingleGame = async (
  eventId: string,
  chainId: number
): Promise<ApiResponse> => {
  if (!eventId) {
    return {
      status: "success",
      data: [],
    }
  }

  const TOKEN = SX_CONFIG[chainId].CONTRACTS.TOKEN
  const response = await fetch(
    `https://api.prod.sx.bet/summary/event/${eventId}/game-lines/${TOKEN}`,
    {
      headers: createHeaders(),
    }
  )

  console.log({ response })

  return response.json()
}

export const checkSystemStatus = async (): Promise<boolean> => {
  try {
    const { data } = await axios.get("https://status.sx.bet/api/v2/status.json")
    return data?.status?.indicator === "none"
  } catch (error) {
    console.error("Status check failed:", error)
    return false
  }
}
